import React, { useEffect, useState } from "react";
import withStyles from "@mui/styles/withStyles";
import { Box, Button, Input, Popover, Typography } from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import DOMPurify from "dompurify";
import classNames from "classnames";

const styles = (theme) => ({
  post: {
    // border: "2px solid " + theme.palette.primary.main,
    // borderRadius: "8px",
    // padding: theme.spacing(2),
    margin: "1rem auto",
    width: "100%",
  },

  nameBox: {
    color: theme.palette.primary.main,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  postBox: {
    margin: theme.spacing(2, 1),
    display: "flex",
    flexDirection: "column",
    // gap: theme.spacing(2),
  },

  postImg: {
    height: "100%",
    maxHeight: "500px",
    width: "100%",
    aspectRatio: "16/9",
    objectFit: "cover",
    borderRadius: "8px",
  },

  smallText: {
    color: "gray",
    fontSize: "0.75rem",
    fontWeight: 500,
    marginLeft: "10px",
  },

  reactsBox: {
    display: "flex",
    gap: theme.spacing(5),
  },

  popover: {
    display: "flex",
    flexDirection: "column",
    "& span": {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "5px",
      padding: theme.spacing(1, 2),
      fontWeight: "600",
    },
  },

  replyField: {
    padding: "1rem",
    border: "2px solid " + theme.palette.primary.main,
    borderRadius: "8px",
    width: "100%",
    margin: theme.spacing(2, 0),
    background: "transparent",
  },

  replyFieldContainer: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    alignItems: "center",
  },

  likeIcon: {
    transform: "scale(1)",
    transition: "100ms",
  },

  likeActive: {
    transform: "scale(1.2)",
  },
});

const Post = ({ classes, post, currentUser, likePost }) => {
  const [popover, setPopover] = useState(false);
  const [openReplies, setOpenReplies] = useState(false);
  const [totalLikes, setTotalLikes] = useState(post?.likeCount);
  const [likedByUser, setLikedByUser] = useState(post?.userHasLiked);
  const history = useHistory();
  const [isAnimating, setIsAnimating] = useState(false);

  const handlePopover = (e) => {
    setPopover(popover ? false : e.target);
  };

  const handleDeletePost = async () => {
    try {
      await axios.delete(`/api/socials/posts/delete/${post.slug}`);
      history.push("/community");
    } catch (error) {
      console.error(error);
    }
  };

  const handleGoToEdit = () => {
    history.push({
      pathname: "/community/create",
      state: {
        post: post,
        edit: true,
      },
    });
  };

  let date = new Date(post.date);
  let day = String(date.getDate()).padStart(2, "0");
  let month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  let year = date.getFullYear();
  let formattedDate = `${day}/${month}/${year}`;

  let sanitizedContent = DOMPurify.sanitize(post.content);

  return (
    <div className={classes.post}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box className={classes.nameBox}>
          <Typography fontWeight={600}>
            {post.user.first_name + " " + post.user.last_name}
          </Typography>
          {currentUser?.id === post.user._id && (
            <div>
              <Button disableRipple onClick={(e) => handlePopover(e)}>
                <MoreHorizIcon />
              </Button>
              <Popover
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                anchorEl={popover}
                open={popover}
                onClose={handlePopover}
                disableScrollLock
              >
                <div className={classes.popover}>
                  <span
                    style={{ color: "graytext" }}
                    onClick={() => handleGoToEdit()}
                  >
                    <EditIcon /> Edit
                  </span>
                  <span
                    style={{ color: "red" }}
                    onClick={() => handleDeletePost()}
                  >
                    <DeleteIcon /> Delete
                  </span>
                </div>
              </Popover>
            </div>
          )}
        </Box>

        <Box className={classes.postBox}>
          {post.title && (
            <div>
              <Typography variant="h6" fontWeight={600}>
                {post.title}

                <span className={classes.smallText}>
                  (Created: {formattedDate})
                </span>
              </Typography>
            </div>
          )}
          <div>
            <Typography
              variant="body1"
              className={classes.postTextOverview}
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
            <Typography>
              {!post.title && (
                <span className={classes.smallText}>(2 months ago)</span>
              )}
            </Typography>
          </div>

          {post.imageURL && (
            <img className={classes.postImg} src={post.imageURL} alt="post" />
          )}
        </Box>

        <Box className={classes.reactsBox}>
          <Typography
            className={classes.smallText}
            sx={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <ForumOutlinedIcon sx={{ cursor: "pointer" }} /> {post.replyCount}{" "}
            Replies
          </Typography>

          <Typography
            className={classes.smallText}
            sx={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            {likedByUser ? (
              <ThumbUpIcon
                sx={{
                  cursor: "pointer",
                  color: "#9461FF",
                }}
                onClick={() => {
                  setIsAnimating(true);
                  setTimeout(() => {
                    setIsAnimating(false);
                  }, 100);
                  setLikedByUser(false);
                  setTotalLikes(totalLikes - 1);
                  likePost("post", post._id);
                }}
                className={classNames(
                  classes.likeIcon,
                  isAnimating && classes.likeActive
                )}
              />
            ) : (
              <ThumbUpOutlinedIcon
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => {
                  setIsAnimating(true);
                  setTimeout(() => {
                    setIsAnimating(false);
                  }, 100);
                  setLikedByUser(true);
                  setTotalLikes(totalLikes + 1);
                  likePost("post", post._id);
                }}
              />
            )}
            {totalLikes} Likes
          </Typography>
        </Box>
      </Box>
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(Post);
